import { createStore, StoreOptions, useStore as baseUseStore } from "vuex";
import { BookingState, GetterFunctions, Mutations, TypedStore, OptionsChanges, BalancePaymentStatus, BalancePaymentSummary } from "./BookingStore.types";
import { InitParams } from "@/main.types";
import { getLnagForLocale } from "@/utils/locale";
import { isCSE } from "@/utils/cse";
import { Booking, Option, Traveller } from "../../../requests-module/src/booking-state/BookingV2";
import { LuggageDetails, Transport } from '../../../requests-module/src/booking-state/Transport';
import { FamilyId, StatusV2, UserInfo } from "../../../requests-module/src";
import { Transfer } from "@/service/transferService/Transfer.types";
import UserInfoFormEnum from "@/config/UserInfoFormEnum";
import LoginForm from "@/model/Form/LoginForm"
import EditForm from "@/model/Form/EditForm";
import RegisterForm from "@/model/Form/RegisterForm";
import { ServiceInfo } from "@/service/CatalogApiService/moelService";
import { SelectedFilter } from "@/components/common/Filters/v2/FilterMapper.types";
import { Locale } from "@/service/translate/flights/flightTranslations.types";
import { FlightSortBy } from "@/components/flightPage/FlightFilters/v2/types.t";
import { trackAddToCartPushCart, trackCartUpdate, trackLoginStep1, trackLoginStep2, trackRemoveFromCart, trackUserInfos } from "@/tracking_v2";
import { PaymentApiResponse } from "@/logic/payment/stripe/v2/OrchestratorService";
import { isAcceptChildren } from "@/utils/isAcceptChildren";
import { defaultWidgetModeState, WidgetModeState } from "./widgetMode";
import { SingleCompanyConfiguration } from "@/types/singleCompanyConfiguration";

const initialState: BookingState = {
    locale: 'fr-FR',
    lang: 'fr',
    isCSE: false,
    acceptChildren: true,

    booking: null,
    unsavedOptions: null,
    product: null,
    flightsAvailability: null,
    transferAvailability: null,
    servicesInfo: null,
    token: null,
    appliedFilters: [],
    flightSortBy: null,
    luggageDetails: null,
    lastUnselectedOption: null,
    lastSelectedOption: null,

    loggedUser: null,
    userForm: UserInfoFormEnum.Check,
    email: '',
    loginForm: new LoginForm(''),
    editForm: new EditForm('fr-FR'),
    registerForm: new RegisterForm('', 'fr-FR'),
    userNamesForPayment: { firstName: '', lastName: '' },
    isPasswordIncorrect: false,
    isAcceptedCondition: false,
    isInsuranceClicked: false,
    isDisplayLuggagePopup: false,
    isLuggagePanelOpened: false,
    isSubscribeNewsletter: { sms: true, email: true },
    initPaymentResponse: null,
    singleCompanyConfiguration: null,

    showSessionExpired: false,
    showSummeryModal: false,
    showChangesModal: false,
    showRemoveParticipantModal: false,
    showFlightErrorModal: false,
    showPromocodeRemovedModal: false,
    isLoading: false,
    optionsChanges: null,
    balancePayment: {
        status: 'loading',
        summary: null,
        currency: 'EUR',
        errorMessage: '',
    },
    widgetMode: defaultWidgetModeState(),
};

const getters: GetterFunctions = {
    getState: (state: BookingState) => state,
    getLocale: (state: BookingState) => state.locale,
    getLang: (state: BookingState) => state.lang,
    getIsCSE: (state: BookingState) => state.isCSE,
    getAcceptChildren: (state: BookingState) => state.acceptChildren,

    getToken: (state: BookingState) => state.token,
    getBooking: (state: BookingState) => state.booking,
    getUnsavedOptions: (state: BookingState) => state.unsavedOptions,
    getProduct: (state: BookingState) => state.product,
    getFlightsAvailability: (state: BookingState) => state.flightsAvailability,
    getTransferAvailability: (state: BookingState) => state.transferAvailability,
    getServicesInfo: (state: BookingState) => state.servicesInfo,
    getServiceInfo: (state: BookingState) => (optionsId: string) => {
        return state.servicesInfo?.find(info => info.id === optionsId) || null;
    }, getIsAcceptedCondition: (state: BookingState) => state.isAcceptedCondition,
    getIsSubscribeNewsletter: (state: BookingState) => state.isSubscribeNewsletter,
    getAppliedFilters: (state: BookingState) => state.appliedFilters,
    getFlightSortBy: (state: BookingState) => state.flightSortBy,

    getLuggageDetails: (state: BookingState) => state.luggageDetails,
    getOptionsChanges: (state: BookingState) => state.optionsChanges,
    getLastSelectedOption: (state: BookingState) => state.lastSelectedOption,
    getLastUnselectedOption: (state: BookingState) => state.lastUnselectedOption,
    getLoggedUser: (state: BookingState) => state.loggedUser,
    getUserForm: (state: BookingState) => state.userForm,
    getEmail: (state: BookingState) => state.email,
    getLoginForm: (state: BookingState) => state.loginForm,
    getEditForm: (state: BookingState) => state.editForm,
    getRegisterForm: (state: BookingState) => state.registerForm,
    getUserNamesForPayment: (state: BookingState) => state.userNamesForPayment,
    getIsPasswordIncorrect: (state: BookingState) => state.isPasswordIncorrect,
    getIsLoading: (state: BookingState) => state.isLoading,
    getShowSessionExpired: (state: BookingState) => state.showSessionExpired,
    getShowSummeryModal: (state: BookingState) => state.showSummeryModal,
    getIsInsuranceClicked: (state: BookingState) => state.isInsuranceClicked,
    getIsDisplayLuggagePopup: (state: BookingState) => state.isDisplayLuggagePopup,
    getIsLuggagePanelOpened: (state: BookingState) => state.isLuggagePanelOpened,
    getInitPaymentResponse: (state: BookingState) => state.initPaymentResponse,
    getSingleCompanyConfiguration: (state: BookingState) => state.singleCompanyConfiguration,
    getShowChangesModal: (state: BookingState) => state.showChangesModal,
    getShowRemoveParticipantModal: (state: BookingState) => state.showRemoveParticipantModal,
    getShowFlightErrorModal: (state: BookingState) => state.showFlightErrorModal,
    getShowPromocodeRemovedModal: (state: BookingState) => state.showPromocodeRemovedModal,
    getBalancePayment: (state: BookingState) => state.balancePayment,
    getIsWidgetMode: (state: BookingState) => state.widgetMode.enabled,
    getWidgetCheckoutMode: (state: BookingState) => state.widgetMode.checkoutMode,
    getWidgetMeta: (state: BookingState) => state.widgetMode,
};


// Define mutations (setters)
const mutations: Mutations = {
    setInitParams: (state: BookingState, initParams: InitParams) => {

        state.locale = initParams.locale;
        state.lang = getLnagForLocale(initParams.locale, window.location.hostname);
        state.isCSE = isCSE(initParams.locale);
        state.editForm.locale = initParams.locale
        state.registerForm.locale = initParams.locale
        state.acceptChildren = isAcceptChildren(initParams.website);
    },
    setLocale: (state: BookingState, locale: Locale) => {
        state.locale = locale;
    },
    setToken: (state: BookingState, token: string) => {
        state.token = token;
    },
    setBooking: (state: BookingState, booking: Booking) => {
        // booking.status = StatusV2.FAILED
        booking.options = booking.options.map(option => {
            if (option.familyId === FamilyId.FLIGHT_LUGGAGE) {
                const details = option.details as LuggageDetails;
                option.details = {
                    ...details,
                    description: details.description?.replace(/(?=[A-Z])/g, ' ') || details.description || '',
                    weight: details.weight ?? 20 //20kg is the default weight
                };
            }
            return option;
        });
        state.booking = booking;
        if (!state.unsavedOptions) {
            state.unsavedOptions = booking.options;
        }
    },
    setUnsavedOptions: (state: BookingState, unsavedOptions: Option[] | null) => {
        state.unsavedOptions = unsavedOptions;
    },
    setProduct: (state: BookingState, product: any) => {
        state.product = product;
    },
    setFlightsAvailability: (state: BookingState, flightsAvailability: Transport[]) => {
        state.flightsAvailability = flightsAvailability;
    },
    setTransferAvailability: (state: BookingState, transferAvailability: Transfer[]) => {
        state.transferAvailability = transferAvailability;
    },
    setLuggageDetails: (state: BookingState, luggageDetails: LuggageDetails[] | null) => {
        state.luggageDetails = luggageDetails;
    },
    setServicesInfo: (state: BookingState, servicesInfo: ServiceInfo[] | null) => {
        state.servicesInfo = servicesInfo;
    },
    setLoggedUser: (state: BookingState, loggedUser: Partial<UserInfo> | null) => {
        state.loggedUser = loggedUser;
        state.email = loggedUser.email
    },
    setUserForm: (state: BookingState, userForm: UserInfoFormEnum) => {
        state.userForm = userForm;

        switch (userForm) {
            case UserInfoFormEnum.LoggedIn:
            case UserInfoFormEnum.Check:
                trackLoginStep1()
                break;
            case UserInfoFormEnum.Login:
                trackLoginStep2()
                break;
            case UserInfoFormEnum.Register:
                trackUserInfos()
                break;
            case UserInfoFormEnum.Edit:
            case UserInfoFormEnum.Reset:
            case UserInfoFormEnum.None:
        }
    },
    setEmail: (state: BookingState, email: string) => {
        state.email = email;
        state.loginForm.email = email;
    },
    setLoginForm: (state: BookingState, loginForm: LoginForm) => {
        state.loginForm = loginForm;
    },
    setEditForm: (state: BookingState, editForm: EditForm) => {
        if (editForm.phone_code && editForm.phone_number && editForm.phone_number.startsWith(editForm.phone_code)) {
            editForm.phone_number = editForm.phone_number.replace(editForm.phone_code, '')
        }
        state.editForm = editForm;
    },
    setRegisterForm: (state: BookingState, registerForm: RegisterForm) => {
        state.registerForm = registerForm;
    },
    setUserNamesForPayment: (state: BookingState, userNamesForPayment: { firstName: string, lastName: string }) => {
        state.userNamesForPayment = userNamesForPayment;
    },
    setAppliedFilters: (state: BookingState, appliedFilters: SelectedFilter[]) => {
        state.appliedFilters = appliedFilters;
    },
    setFlightSortBy: (state: BookingState, flightSortBy: FlightSortBy | null) => {
        state.flightSortBy = flightSortBy;
    },
    setIsPasswordIncorrect: (state: BookingState, isPasswordIncorrect: boolean) => {
        state.isPasswordIncorrect = isPasswordIncorrect;
    },
    setIsAcceptedCondition: (state: BookingState, isAcceptedCondition: boolean) => {
        state.isAcceptedCondition = isAcceptedCondition;
    },
    setIsSubscribeNewsletter: (state: BookingState, isSubscribeNewsletter: { sms: boolean, email: boolean }) => {
        state.isSubscribeNewsletter = isSubscribeNewsletter;
    },
    setIsInsuranceClicked: (state: BookingState, isInsuranceClicked: boolean) => {
        state.isInsuranceClicked = isInsuranceClicked;
    },
    setIsDisplayLuggagePopup: (state: BookingState, isDisplayLuggagePopup: boolean) => {
        state.isDisplayLuggagePopup = isDisplayLuggagePopup;
    },
    setIsLuggagePanelOpened: (state: BookingState, isLuggagePanelOpened: boolean) => {
        state.isLuggagePanelOpened = isLuggagePanelOpened;
    },
    setLastUnselectedOption: (state: BookingState, lastUnselectedOption: Option) => {
        state.lastUnselectedOption = lastUnselectedOption;
        trackRemoveFromCart()
        trackCartUpdate()
    },
    setLastSelectedOption: (state: BookingState, lastSelectedOption: Option) => {
        state.lastSelectedOption = lastSelectedOption;
        trackAddToCartPushCart()
        trackCartUpdate()
    },
    setShowSessionExpired: (state: BookingState, showSessionExpired: false | string) => {
        state.showSessionExpired = showSessionExpired;
    },
    setShowSummeryModal: (state: BookingState, showSummeryModal: boolean) => {
        state.showSummeryModal = showSummeryModal;
    },
    setIsLoading: (state: BookingState, isLoading: boolean) => {
        state.isLoading = isLoading;
    },
    setInitPaymentResponse: (state: BookingState, initPaymentResponse: PaymentApiResponse | null) => {
        state.initPaymentResponse = initPaymentResponse;
    },
    setSingleCompanyConfiguration: (state: BookingState, singleCompanyConfiguration: SingleCompanyConfiguration | null) => {
        state.singleCompanyConfiguration = singleCompanyConfiguration;
    },
    setOptionsChanges: (state: BookingState, optionsChanges: { changes?: OptionsChanges[], travellers: { before: Traveller[], after: Traveller[] } } | null) => {
        state.optionsChanges = optionsChanges;
    },
    setShowChangesModal: (state: BookingState, showChangesModal: boolean) => {
        state.showChangesModal = showChangesModal;
    },
    setShowRemoveParticipantModal: (state: BookingState, showRemoveParticipantModal: boolean) => {
        state.showRemoveParticipantModal = showRemoveParticipantModal;
    },
    setShowFlightErrorModal: (state: BookingState, showFlightErrorModal: boolean) => {
        state.showFlightErrorModal = showFlightErrorModal;
    },
    setShowPromocodeRemovedModal: (state: BookingState, showPromocodeRemovedModal: boolean) => {
        state.showPromocodeRemovedModal = showPromocodeRemovedModal;
    },
    setBalancePaymentStatus: (state: BookingState, status: BalancePaymentStatus) => {
        state.balancePayment.status = status;
    },
    setBalancePaymentSummary: (state: BookingState, summary: BalancePaymentSummary | null) => {
        state.balancePayment.summary = summary;
    },
    setBalancePaymentCurrency: (state: BookingState, currency: string) => {
        state.balancePayment.currency = currency;
    },
    setBalancePaymentError: (state: BookingState, errorMessage: string) => {
        state.balancePayment.errorMessage = errorMessage;
        state.balancePayment.status = 'error';
    },
    setWidgetMode: (state: BookingState, payload: WidgetModeState) => {
        state.widgetMode = { ...payload };
    },
};


// Define the store options with explicit types
const storeOptions: StoreOptions<BookingState> = {
    state: initialState,
    getters,
    mutations,
};

const store = createStore<BookingState>(storeOptions);

// Watch for promocode removal at store level
let previousPromocodeId: string | null = null;

// Initialize previous promocode state on store creation
const initializePromocodeWatcher = () => {
    if (store.state.booking) {
        const promocodeOption = store.state.booking.options?.find(
            (option: Option) => option.familyId === FamilyId.PRICE_ADJUSTMENT_PROMO
        );
        previousPromocodeId = promocodeOption ? promocodeOption.id : null;
    }

    // Watch for booking changes
    store.watch(
        (state) => state.booking,
        (newBooking: Booking | null) => {
            if (!newBooking) {
                previousPromocodeId = null;
                return;
            }

            // Get current promocode option
            const newPromocodeOption = newBooking.options?.find(
                (option: Option) => option.familyId === FamilyId.PRICE_ADJUSTMENT_PROMO
            );
            const newPromocodeId = newPromocodeOption ? newPromocodeOption.id : null;

            // Check if old booking had a promocode but new doesn't
            if (previousPromocodeId && !newPromocodeId) {
                // Show the modal
                store.commit('setShowPromocodeRemovedModal', true);
            }

            // Update previous state reference
            previousPromocodeId = newPromocodeId;
        },
        { deep: true }
    );
};

// Initialize the watcher
initializePromocodeWatcher();

export function useStore(): TypedStore {
    return baseUseStore() as TypedStore;
}
export default store as TypedStore;
